import { EU_ALIAS_CODE, isEuCountry } from '@/domain/countries'
import { RoutingRule, alwaysMatches } from './warehouse'

// Structured view of the settings-api business-rule expressions a routing rule stores in `whens`:
// a rule is a list of conditions that must all match. There is no OR: alternatives are separate rules.

export type ConditionCategory = 'address' | 'return' | 'product' | 'order'

export type FieldKind =
  | 'list'
  | 'text'
  | 'boolean'
  | 'number'
  | 'money'
  | 'date'

export type ValueSource =
  | 'countries'
  // Countries plus settings-api's "EU" alias, which only returnAddressCountryIn understands.
  | 'returnCountries'
  | 'returnMethods'
  | 'returnReasons'
  | 'refundMethods'
  | 'free'

interface FieldSpec {
  category: ConditionCategory
  kind: FieldKind
  fn: string
  values?: ValueSource
}

// Every function settings-api offers in the DynamicWarehouseIDs context (business_rules_registry.json).
export const CONDITION_FIELDS = {
  country: {
    category: 'address',
    kind: 'list',
    fn: 'returnAddressCountryIn',
    values: 'returnCountries'
  },
  postalCode: {
    category: 'address',
    kind: 'list',
    fn: 'returnAddressPostalCodeStartsWith',
    values: 'free'
  },
  returnMethod: {
    category: 'return',
    kind: 'list',
    fn: 'returnMethodIncludes',
    values: 'returnMethods'
  },
  returnReason: {
    category: 'return',
    kind: 'list',
    fn: 'returnReasonsContain',
    values: 'returnReasons'
  },
  returnValue: { category: 'return', kind: 'money', fn: 'totalReturnAmount' },
  productTag: {
    category: 'product',
    kind: 'list',
    fn: 'productTagsContain',
    values: 'free'
  },
  productTagTwoOrMore: {
    category: 'product',
    kind: 'list',
    fn: 'productTagsContainTwoOrMore',
    values: 'free'
  },
  productTagOnlyOne: {
    category: 'product',
    kind: 'list',
    fn: 'theBraderyOneProductTag',
    values: 'free'
  },
  productId: {
    category: 'product',
    kind: 'list',
    fn: 'productIDsContain',
    values: 'free'
  },
  variantId: {
    category: 'product',
    kind: 'list',
    fn: 'variantIDsContain',
    values: 'free'
  },
  productCategory: {
    category: 'product',
    kind: 'list',
    fn: 'productCategoriesContain',
    values: 'free'
  },
  productCollection: {
    category: 'product',
    kind: 'list',
    fn: 'productCollectionsContain',
    values: 'free'
  },
  orderTag: {
    category: 'order',
    kind: 'list',
    fn: 'orderTagIn',
    values: 'free'
  },
  orderCountry: {
    category: 'order',
    kind: 'text',
    fn: 'orderCountry',
    values: 'countries'
  },
  orderFromEU: {
    category: 'order',
    kind: 'boolean',
    fn: 'orderIsFromEuropeanUnion'
  },
  orderPointOfSale: {
    category: 'order',
    kind: 'boolean',
    fn: 'orderIsPointOfSale'
  },
  orderExchange: { category: 'order', kind: 'boolean', fn: 'orderIsExchange' },
  orderPaymentGateway: {
    category: 'order',
    kind: 'text',
    fn: 'orderPaymentGateway',
    values: 'free'
  },
  orderNumber: {
    category: 'order',
    kind: 'text',
    fn: 'orderCustomerPrintedOrderID',
    values: 'free'
  },
  orderProductCategory: {
    category: 'order',
    kind: 'list',
    fn: 'orderContainsCategoryID',
    values: 'free'
  },
  orderProductTag: {
    category: 'order',
    kind: 'list',
    fn: 'productTagIDIn',
    values: 'free'
  },
  orderTotal: { category: 'order', kind: 'money', fn: 'orderTotal' },
  orderDaysSinceBought: {
    category: 'order',
    kind: 'number',
    fn: 'orderDaysSinceBought'
  },
  purchaseDate: { category: 'order', kind: 'date', fn: 'orderBoughtBefore' },
  orderBNPL: {
    category: 'order',
    kind: 'boolean',
    fn: 'orderIsBuyNowPayLater'
  },
  orderOPMRefund: {
    category: 'order',
    kind: 'boolean',
    fn: 'orderAllowsOPMRefund'
  },
  orderStoreCredit: {
    category: 'order',
    kind: 'boolean',
    fn: 'orderIsPaidWithPromocodesOrGiftCards'
  },
  orderProductCollection: {
    category: 'order',
    kind: 'list',
    fn: 'orderProductCollectionIDIn',
    values: 'free'
  },
  orderProductId: {
    category: 'order',
    kind: 'list',
    fn: 'orderProductIDIn',
    values: 'free'
  },
  returnExchange: {
    category: 'return',
    kind: 'boolean',
    fn: 'returnHasExchange'
  },
  refundMethod: {
    category: 'return',
    kind: 'list',
    fn: 'refundPaymentMethodIn',
    values: 'refundMethods'
  },
  refundMethodWithAmount: {
    category: 'return',
    kind: 'list',
    fn: 'atLeastOneReturnMethodFrom',
    values: 'refundMethods'
  },
  opmAmount: { category: 'return', kind: 'money', fn: 'opmAmount' },
  exchangeAmount: { category: 'return', kind: 'money', fn: 'exchangeAmount' },
  giftCardAmount: { category: 'return', kind: 'money', fn: 'giftCardAmount' },
  promocodeAmount: { category: 'return', kind: 'money', fn: 'promocodeAmount' },
  bankTransferAmount: {
    category: 'return',
    kind: 'money',
    fn: 'bankTransferAmount'
  },
  categoryFullyReturned: {
    category: 'product',
    kind: 'list',
    fn: 'productsWithCategoryIDAreFullyReturned',
    values: 'free'
  }
} satisfies Record<string, FieldSpec>

export type ConditionField = keyof typeof CONDITION_FIELDS

export const fieldSpec = (field: ConditionField): FieldSpec =>
  CONDITION_FIELDS[field]

// The fields each settings-api rule context offers (business_rules_registry.json); IsReturnEnabled offers the
// same ones as IsExchangeEnabled.
export const RULE_CONTEXT_FIELDS = {
  RefundMethods: [
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'orderTag',
    'orderCountry',
    'orderFromEU',
    'orderPointOfSale',
    'orderExchange',
    'orderPaymentGateway',
    'orderNumber',
    'orderProductCategory',
    'orderProductTag',
    'orderTotal',
    'orderDaysSinceBought',
    'purchaseDate',
    'orderBNPL',
    'orderOPMRefund',
    'orderStoreCredit',
    'orderProductCollection',
    'orderProductId',
    'categoryFullyReturned'
  ],
  IsExchangeEnabled: [
    'returnReason',
    'returnValue',
    'productTag',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'orderTag',
    'orderCountry',
    'orderFromEU',
    'orderPointOfSale',
    'orderExchange',
    'orderPaymentGateway',
    'orderNumber',
    'orderProductCategory',
    'orderProductTag',
    'orderTotal',
    'orderDaysSinceBought',
    'purchaseDate',
    'orderBNPL',
    'orderOPMRefund',
    'orderStoreCredit',
    'orderProductCollection',
    'orderProductId'
  ],
  PayForLogistics: [
    'country',
    'postalCode',
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'orderTag',
    'orderCountry',
    'orderFromEU',
    'orderPointOfSale',
    'orderExchange',
    'orderPaymentGateway',
    'orderNumber',
    'orderProductCategory',
    'orderProductTag',
    'orderTotal',
    'orderDaysSinceBought',
    'purchaseDate',
    'orderBNPL',
    'orderOPMRefund',
    'orderStoreCredit',
    'orderProductCollection',
    'orderProductId',
    'refundMethodWithAmount',
    'opmAmount',
    'exchangeAmount',
    'giftCardAmount',
    'promocodeAmount',
    'bankTransferAmount'
  ],
  RequireAuthorization: [
    'country',
    'postalCode',
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'orderTag',
    'orderCountry',
    'orderFromEU',
    'orderPointOfSale',
    'orderExchange',
    'orderPaymentGateway',
    'orderNumber',
    'orderProductCategory',
    'orderProductTag',
    'orderTotal',
    'orderDaysSinceBought',
    'purchaseDate',
    'orderBNPL',
    'orderOPMRefund',
    'orderStoreCredit',
    'orderProductCollection',
    'orderProductId',
    'refundMethodWithAmount',
    'opmAmount',
    'exchangeAmount',
    'giftCardAmount',
    'promocodeAmount',
    'bankTransferAmount'
  ],
  UploadDocuments: [
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'orderTag',
    'orderCountry',
    'orderFromEU',
    'orderPointOfSale',
    'orderExchange',
    'orderPaymentGateway',
    'orderNumber',
    'orderProductCategory',
    'orderProductTag',
    'orderTotal',
    'orderDaysSinceBought',
    'purchaseDate',
    'orderBNPL',
    'orderOPMRefund',
    'orderStoreCredit',
    'orderProductCollection',
    'orderProductId'
  ],
  KeepYourItemRefundOption: [
    'country',
    'postalCode',
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection',
    'returnExchange',
    'refundMethod'
  ],
  RDVSurplus: [
    'country',
    'postalCode',
    'returnReason',
    'returnValue',
    'productTag',
    'productTagTwoOrMore',
    'productTagOnlyOne',
    'productId',
    'variantId',
    'productCategory',
    'productCollection'
  ]
} satisfies Record<string, ConditionField[]>

export type RuleContext = keyof typeof RULE_CONTEXT_FIELDS

export const CONDITION_CATEGORIES: ConditionCategory[] = [
  'address',
  'return',
  'product',
  'order'
]

export type Operator =
  | 'in'
  | 'notIn'
  | 'is'
  | 'isNot'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'eq'
  | 'neq'
  | 'before'
  | 'after'
  | 'onOrAfter'
  | 'onOrBefore'

export const OPERATORS: Record<FieldKind, Operator[]> = {
  list: ['in', 'notIn'],
  text: ['in', 'notIn'],
  boolean: ['is', 'isNot'],
  number: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'],
  money: ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'],
  date: ['before', 'after', 'onOrAfter', 'onOrBefore']
}

// Lists hold any number of values, booleans none, the rest exactly one.
export interface Condition {
  field: ConditionField
  operator: Operator
  values: string[]
}

export const newCondition = (field: ConditionField): Condition => ({
  field,
  operator: OPERATORS[fieldSpec(field).kind][0],
  values: []
})

export const RETURN_METHODS = [
  'HOME_PICK_UP',
  'DROP_OFF_POINT',
  'DELIVERY_PICKUP',
  'IN_STORE'
]

export const RETURN_REASONS = [
  'WRONG_SIZE_TOO_SMALL',
  'WRONG_SIZE_TOO_BIG',
  'FIT_NOT_AS_EXPECTED',
  'CHANGED_MY_MIND',
  'DEFECTIVE',
  'WRONG_ITEM',
  'QUALITY_NOT_AS_EXPECTED',
  'PRODUCT_DID_NOT_MATCH_DESCRIPTION',
  'ARRIVED_LATE',
  'NOT_RECEIVED',
  'OTHER'
]

// The rule's conditions, or undefined when it can't be shown as one list (an OR, an unknown function,
// unparseable text): such a rule is kept exactly as written and can't be edited here.
export function toConditions(whens: string[]): Condition[] | undefined {
  let groups: Literal[][] | undefined = [[]]
  for (const when of whens) {
    const ast = parse(when)
    const dnf = ast && toDnf(ast, false)
    groups = dnf && groups && product(groups, dnf)
    if (!groups) return undefined
  }
  if (groups.length !== 1) return undefined
  const conditions = groups[0].map(toCondition)
  return conditions.every(Boolean) ? (conditions as Condition[]) : undefined
}

// One expression per condition: settings-api ANDs them.
export const toWhens = (conditions: Condition[]): string[] =>
  conditions.map(toExpression)

export function isComplete(condition: Condition) {
  const kind = fieldSpec(condition.field).kind
  if (kind === 'boolean') return true
  if (kind === 'list' || kind === 'text') return condition.values.length > 0
  const value = condition.values[0] ?? ''
  // settings-api also reads DD/MM/YYYY dates.
  return kind === 'date'
    ? /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/.test(value)
    : value.trim() !== '' && !isNaN(Number(value))
}

// Every condition of the rule has its value; a rule kept as written is left alone.
export function whensComplete(whens: string[]) {
  return toConditions(whens)?.every(isComplete) ?? true
}

// A rule above the default needs a condition (else it shadows every rule below it), every condition
// a value, and every rule a warehouse. A rule kept as written is left alone.
export function isRuleComplete(rule: RoutingRule, isDefault: boolean) {
  if (!rule.warehouseId) return false
  if (isDefault) return true
  const conditions = toConditions(rule.whens)
  if (!conditions) return true
  return conditions.length > 0 && conditions.every(isComplete)
}

// The last rule is the default when it always matches; without one, a return matching no rule fails.
export const splitDefault = <T extends RoutingRule>(rules: T[]) => {
  const last = rules.at(-1)
  return last && alwaysMatches(last)
    ? { rules: rules.slice(0, -1), fallback: last }
    : { rules, fallback: undefined }
}

// A test return is one returned item, as typed: list fields take several values separated by commas,
// amounts are in currency units, booleans 'true' or 'false', and the purchase date YYYY-MM-DD.
export type TestReturn = Partial<Record<ConditionField, string>>

export const NEW_TEST_RETURN: TestReturn = {
  country: 'ES',
  returnMethod: 'HOME_PICK_UP'
}

// Fields settings-api reads from another field's fact: the EU from the order's shipping country, and
// every product tag condition from the item's tags.
const ASKED_AS: Partial<Record<ConditionField, ConditionField>> = {
  orderFromEU: 'orderCountry',
  productTagTwoOrMore: 'productTag',
  productTagOnlyOne: 'productTag'
}

// The fields the test asks for: the return address and method, plus every field a rule uses.
export function testFields(rules: RoutingRule[]): ConditionField[] {
  const used = new Set<ConditionField>(['country', 'returnMethod'])
  for (const rule of rules)
    for (const { field } of toConditions(rule.whens) ?? [])
      used.add(ASKED_AS[field] ?? field)
  return (Object.keys(CONDITION_FIELDS) as ConditionField[]).filter((field) =>
    used.has(field)
  )
}

function factsOf(test: TestReturn, field: ConditionField): string[] {
  if (field === 'orderFromEU') {
    const country = test.orderCountry?.trim().toUpperCase()
    return country ? [String(isEuCountry(country))] : []
  }
  const text = test[ASKED_AS[field] ?? field]?.trim() ?? ''
  if (fieldSpec(field).kind !== 'list') return text ? [text] : []
  return text
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
}

// Mirrors the settings-api function behind each list field.
function listHit(field: ConditionField, facts: string[], values: string[]) {
  const has = (value: string) =>
    facts.some((fact) => fact.toLowerCase() === value.toLowerCase())
  const distinctFound = () =>
    new Set(values.filter(has).map((value) => value.toLowerCase())).size
  switch (field) {
    case 'country':
      return values.some(
        (value) =>
          has(value) ||
          (value.toUpperCase() === EU_ALIAS_CODE && facts.some(isEuCountry))
      )
    case 'postalCode':
      return values.some((prefix) =>
        facts.some((fact) => fact.toUpperCase().startsWith(prefix))
      )
    case 'productTagTwoOrMore':
      return distinctFound() >= 2
    case 'productTagOnlyOne':
      return distinctFound() === 1
    default:
      return values.some(has)
  }
}

function compare(operator: Operator, a: number, b: number) {
  switch (operator) {
    case 'gt':
      return a > b
    case 'gte':
      return a >= b
    case 'lt':
      return a < b
    case 'lte':
      return a <= b
    case 'eq':
      return a === b
    case 'neq':
      return a !== b
    default:
      return false
  }
}

// Fields left empty read as unknown and never match; a rule kept as written can't be run.
export function matchesTest(rule: RoutingRule, test: TestReturn) {
  const conditions = toConditions(rule.whens)
  if (!conditions) return false
  const holds = ({ field, operator, values }: Condition) => {
    const facts = factsOf(test, field)
    if (!facts.length) return false
    const [fact] = facts
    const [value = ''] = values
    const kind = fieldSpec(field).kind
    switch (kind) {
      case 'list':
        return listHit(field, facts, values) === (operator === 'in')
      // gval's `in` compares text exactly.
      case 'text':
        return values.includes(fact) === (operator === 'in')
      case 'boolean':
        return (fact === 'true') === (operator === 'is')
      case 'money':
      case 'number': {
        const typed = Number(fact.replace(',', '.'))
        if (value === '' || isNaN(typed)) return false
        return compare(
          operator,
          kind === 'money' ? Math.round(typed * 100) : typed,
          Number(value)
        )
      }
      case 'date': {
        // settings-api compares the purchase time with the date's midnight, so a purchase on that very
        // day counts as after it; the test buys at noon.
        const bought = `${fact}T12`
        const day = `${value.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1')}T00`
        return operator === 'before' || operator === 'onOrBefore'
          ? bought < day
          : bought > day
      }
    }
  }
  return conditions.every(holds)
}

// ---- expressions -> conditions

type Node =
  | { t: 'or' | 'and'; items: Node[] }
  | { t: 'not'; item: Node }
  | { t: 'const'; value: boolean }
  | { t: 'call'; fn: string; args: string[] }
  | { t: 'cmp'; fn: string; op: string; value: string }
  | { t: 'in'; fn: string; values: string[] }

type Literal = {
  node: Extract<Node, { t: 'call' | 'cmp' | 'in' }>
  negated: boolean
}

const FLIP: Record<string, string> = {
  '>': '<=',
  '>=': '<',
  '<': '>=',
  '<=': '>',
  '==': '!=',
  '!=': '=='
}
const COMPARISON: Record<string, Operator> = {
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  '==': 'eq',
  '!=': 'neq'
}
const SYMBOL = Object.fromEntries(
  Object.entries(COMPARISON).map(([s, o]) => [o, s])
) as Record<Operator, string>

const FIELD_BY_FN = new Map<string, ConditionField>(
  (Object.keys(CONDITION_FIELDS) as ConditionField[]).map((field) => [
    fieldSpec(field).fn,
    field
  ])
)

function toCondition({ node, negated }: Literal): Condition | undefined {
  if (
    node.t === 'call' &&
    (node.fn === 'orderBoughtBefore' || node.fn === 'orderBoughtAfter')
  ) {
    if (node.args.length !== 1) return undefined
    const before = node.fn === 'orderBoughtBefore'
    const operator = before
      ? negated
        ? 'onOrAfter'
        : 'before'
      : negated
        ? 'onOrBefore'
        : 'after'
    return { field: 'purchaseDate', operator, values: node.args }
  }
  const field = FIELD_BY_FN.get(node.fn)
  if (!field) return undefined
  const kind = fieldSpec(field).kind
  if (node.t === 'call') {
    if (kind === 'list')
      return { field, operator: negated ? 'notIn' : 'in', values: node.args }
    if (kind === 'boolean' && !node.args.length)
      return { field, operator: negated ? 'isNot' : 'is', values: [] }
    return undefined
  }
  if (node.t === 'in')
    return kind === 'text'
      ? { field, operator: negated ? 'notIn' : 'in', values: node.values }
      : undefined
  const op = negated ? FLIP[node.op] : node.op
  // `fn() > ""` is how a draft holds an amount or number not typed yet.
  if ((kind === 'number' || kind === 'money') && node.value === '')
    return { field, operator: COMPARISON[op], values: [] }
  if (kind === 'text' && (op === '==' || op === '!='))
    return {
      field,
      operator: op === '==' ? 'in' : 'notIn',
      values: [node.value]
    }
  if (
    (kind === 'number' || kind === 'money') &&
    /^-?\d+(\.\d+)?$/.test(node.value)
  )
    return { field, operator: COMPARISON[op], values: [node.value] }
  return undefined
}

function toDnf(node: Node, negated: boolean): Literal[][] | undefined {
  switch (node.t) {
    case 'const':
      return node.value !== negated ? [[]] : []
    case 'not':
      return toDnf(node.item, !negated)
    case 'or':
    case 'and': {
      const parts = node.items.map((item) => toDnf(item, negated))
      if (parts.some((part) => !part)) return undefined
      // De Morgan: a negated OR is an AND of negations, and the other way round.
      const disjunction = (node.t === 'or') !== negated
      if (disjunction) return (parts as Literal[][][]).flat()
      return (parts as Literal[][][]).reduce<Literal[][] | undefined>(
        (acc, part) => acc && product(acc, part),
        [[]]
      )
    }
    default:
      return [[{ node, negated }]]
  }
}

// Gives up as soon as a rule needs an OR: no later step can merge groups back into one.
function product(a: Literal[][], b: Literal[][]): Literal[][] | undefined {
  if (a.length * b.length > 1) return undefined
  return a.flatMap((x) => b.map((y) => [...x, ...y]))
}

// ---- conditions -> expressions

const list = (values: string[]) =>
  values.map((value) => JSON.stringify(value)).join(', ')

function toExpression({ field, operator, values }: Condition): string {
  const { kind, fn } = fieldSpec(field)
  switch (kind) {
    case 'list':
      return `${operator === 'notIn' ? '!' : ''}${fn}(${list(values)})`
    case 'text': {
      const test = `${fn}() in [${list(values)}]`
      return operator === 'notIn' ? `!(${test})` : test
    }
    case 'boolean':
      return `${operator === 'isNot' ? '!' : ''}${fn}()`
    case 'date': {
      const call = (name: string) =>
        `${name}(${JSON.stringify(values[0] ?? '')})`
      if (operator === 'before') return call('orderBoughtBefore')
      if (operator === 'after') return call('orderBoughtAfter')
      if (operator === 'onOrAfter') return `!${call('orderBoughtBefore')}`
      return `!${call('orderBoughtAfter')}`
    }
    default:
      return `${fn}() ${SYMBOL[operator]} ${values[0] ?? '""'}`
  }
}

// ---- parser for the gval subset routing rules use

type Token = { k: 'op' | 'ident' | 'string' | 'number'; v: string }

const TOKEN =
  /\s*(?:(&&|\|\||==|!=|>=|<=|[()[\],!<>])|([A-Za-z_]\w*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(-?\d+(?:\.\d+)?))/y

function tokenize(text: string): Token[] | undefined {
  const tokens: Token[] = []
  TOKEN.lastIndex = 0
  while (TOKEN.lastIndex < text.length) {
    if (!text.slice(TOKEN.lastIndex).trim()) break
    const match = TOKEN.exec(text)
    if (!match) return undefined
    if (match[1]) tokens.push({ k: 'op', v: match[1] })
    else if (match[2]) tokens.push({ k: 'ident', v: match[2] })
    else if (match[3]) {
      const literal = match[3]
      try {
        tokens.push({
          k: 'string',
          v:
            literal[0] === '"'
              ? JSON.parse(literal)
              : literal.slice(1, -1).replace(/\\(.)/g, '$1')
        })
      } catch {
        return undefined
      }
    } else tokens.push({ k: 'number', v: match[4] })
  }
  return tokens
}

function parse(text: string): Node | undefined {
  const tokens = tokenize(text)
  if (!tokens?.length) return undefined
  let at = 0
  const peek = (v: string) => tokens[at]?.k === 'op' && tokens[at].v === v
  const take = (v: string) => (peek(v) ? (at++, true) : false)

  const strings = (close: string): string[] | undefined => {
    const values: string[] = []
    if (take(close)) return values
    do {
      const token = tokens[at++]
      if (token?.k !== 'string') return undefined
      values.push(token.v)
    } while (take(','))
    return take(close) ? values : undefined
  }

  const primary = (): Node | undefined => {
    if (take('(')) {
      const inner = or()
      return inner && take(')') ? inner : undefined
    }
    const token = tokens[at++]
    if (token?.k !== 'ident') return undefined
    if (token.v === 'true' || token.v === 'false')
      return { t: 'const', value: token.v === 'true' }
    if (!take('(')) return undefined
    const args = strings(')')
    if (!args) return undefined
    const call: Node = { t: 'call', fn: token.v, args }
    const op = tokens[at]
    if (op?.k === 'op' && op.v in COMPARISON) {
      at++
      const value = tokens[at++]
      // `call() == false` is how some rules spell a negation.
      if (
        value?.k === 'ident' &&
        (value.v === 'true' || value.v === 'false') &&
        (op.v === '==' || op.v === '!=')
      ) {
        return (value.v === 'true') === (op.v === '==')
          ? call
          : { t: 'not', item: call }
      }
      if (args.length || (value?.k !== 'number' && value?.k !== 'string'))
        return undefined
      return { t: 'cmp', fn: token.v, op: op.v, value: value.v }
    }
    if (tokens[at]?.k === 'ident' && tokens[at].v === 'in') {
      at++
      const values = take('[') ? strings(']') : undefined
      return values && !args.length
        ? { t: 'in', fn: token.v, values }
        : undefined
    }
    return call
  }

  const unary = (): Node | undefined => {
    if (take('!')) {
      const item = unary()
      return item && { t: 'not', item }
    }
    return primary()
  }

  const chain = (
    op: '&&' | '||',
    t: 'and' | 'or',
    next: () => Node | undefined
  ) => {
    const items: Node[] = []
    do {
      const item = next()
      if (!item) return undefined
      items.push(item)
    } while (take(op))
    return items.length === 1 ? items[0] : ({ t, items } as Node)
  }
  const and = () => chain('&&', 'and', unary)
  const or = (): Node | undefined => chain('||', 'or', and)

  const node = or()
  return node && at === tokens.length ? node : undefined
}
